import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { MEAL_SLOTS, filterRecipes, lastEatenByRecipe, sortRecipes } from '@kraftwerk/shared';
import type { MealSlot } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalFoodLogEntry, LocalRecipe } from '../../db/local.js';
import { listRecipes } from '../../db/nutrition.js';
import { RecipeMacroLine } from './RecipeMacroLine.js';
import { RecipeImage } from './RecipeImage.js';

/**
 * Die Gerichte als Kacheln: Bild, Name, Werte je Portion.
 *
 * Favoriten stehen oben, darin das zuletzt Gegessene — wer nachschlägt, sucht
 * meist, was er neulich hatte. Suche und Mahlzeit-Filter für den Rest.
 */
export function RecipeGrid({
  onOpen,
  onCreate,
  onImport,
}: {
  onOpen: (id: string) => void;
  onCreate: () => void;
  onImport: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const searchId = useId();
  const [query, setQuery] = useState('');
  const [slot, setSlot] = useState<MealSlot | null>(null);

  // Flach gelesen — eine verschachtelte Abfrage verlöre die Live-Beobachtung.
  const recipes = useLiveQuery(() => listRecipes(db), [], [] as LocalRecipe[]);
  const log = useLiveQuery(() => db.food_log.toArray(), [], [] as LocalFoodLogEntry[]);

  const shown = sortRecipes(filterRecipes(recipes, query, slot), lastEatenByRecipe(log));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label htmlFor={searchId} className="text-xs text-ink-muted">
            {t('dish.search')}
          </label>
          <input
            id={searchId}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="kw-field"
          />
        </div>
        <button type="button" onClick={onCreate} className="kw-btn kw-btn-small shrink-0 px-3">
          {t('nutrition.library.newRecipe')}
        </button>
      </div>
      <button type="button" onClick={onImport} className="kw-btn kw-btn-quiet kw-btn-small">
        {t('import.open')}
      </button>

      <div role="group" aria-label={t('dish.filter')} className="flex flex-wrap gap-1">
        {[null, ...MEAL_SLOTS].map((option) => (
          <button
            key={option ?? 'all'}
            type="button"
            aria-pressed={slot === option}
            onClick={() => setSlot(option)}
            className="kw-toggle"
          >
            {option ? t(`nutrition.slot.${option}`) : t('dish.allSlots')}
          </button>
        ))}
      </div>

      {shown.length > 0 ? (
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {shown.map((recipe) => (
            <li key={recipe.id}>
              <button
                type="button"
                onClick={() => onOpen(recipe.id)}
                className="kw-tile flex h-full w-full flex-col gap-2 overflow-hidden p-0 text-left"
              >
                {/* Ohne Bild ein flacher Streifen statt einer großen leeren Fläche. */}
                <RecipeImage
                  lazy
                  recipe={recipe}
                  className={`w-full ${recipe.imageAt ? 'aspect-[4/3]' : 'aspect-[3/1]'}`}
                />
                <span className="flex flex-col gap-1 px-3 pb-3">
                  <span className="flex items-start gap-1 font-semibold leading-snug">
                    {recipe.isFavorite ? (
                      <span className="text-accent" aria-label={t('dish.favorite')}>
                        ★
                      </span>
                    ) : null}
                    <span className="min-w-0 break-words">{recipe.name}</span>
                  </span>
                  <span className="text-xs text-ink-muted">
                    {t(`nutrition.slot.${recipe.slot}`)}
                  </span>
                  <span className="text-xs">
                    <RecipeMacroLine recipe={recipe} compact />
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-ink-muted">
          {recipes.length === 0 ? t('nutrition.library.noRecipes') : t('dish.noMatch')}
        </p>
      )}
    </div>
  );
}
